import {
  getVlmGatewayProfiles,
  hasMalformedVlmGatewaySection,
  hasMiscasedVlmGatewaySection,
  hasMiscasedVlmProfileField,
  isRecord,
  isValidVlmProfileName,
  VLM_PROFILE_FIELDS,
  VlmProfileRename,
} from './customerSecretStore';

export const VLM_PROVIDERS = ['Claude', 'Gpt', 'Gemini'] as const;

// ApiKey is a secret reference, managed apart from the settings.
export type VlmProfileSettingsField = Exclude<
  (typeof VLM_PROFILE_FIELDS)[number],
  'ApiKey'
>;

export type VlmProfileSettingsForm = Record<VlmProfileSettingsField, string>;

const SETTINGS_FIELDS: readonly VlmProfileSettingsField[] =
  VLM_PROFILE_FIELDS.filter(
    (field): field is VlmProfileSettingsField => field !== 'ApiKey',
  );

const NUMBER_FIELDS = {
  MaxTokens: 1,
  TimeoutSeconds: 1,
  MaxImageDimension: 0,
} as const satisfies Partial<Record<VlmProfileSettingsField, number>>;

const JSON_FIELDS = ['ResponseSchema', 'ProviderOptions'] as const;

// The gateway reads these numbers into a C# int.
const INT32_MAX = 2147483647;

const isNumberField = (
  field: VlmProfileSettingsField,
): field is keyof typeof NUMBER_FIELDS => field in NUMBER_FIELDS;

const isJsonField = (
  field: VlmProfileSettingsField,
): field is (typeof JSON_FIELDS)[number] =>
  (JSON_FIELDS as readonly string[]).includes(field);

const canonicalProvider = (value: string): string | undefined =>
  VLM_PROVIDERS.find(
    (provider) => provider.toLowerCase() === value.toLowerCase(),
  );

const readField = (field: VlmProfileSettingsField, value: unknown): string => {
  if (isNumberField(field)) {
    return typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : '';
  }
  if (isJsonField(field)) {
    return isRecord(value) ? JSON.stringify(value, null, 2) : '';
  }
  if (typeof value !== 'string') {
    return '';
  }
  return field === 'Provider' ? (canonicalProvider(value) ?? value) : value;
};

export const readVlmProfileSettings = (
  profile: unknown,
): VlmProfileSettingsForm => {
  const record = isRecord(profile) ? profile : {};
  return Object.fromEntries(
    SETTINGS_FIELDS.map((field) => [field, readField(field, record[field])]),
  ) as VlmProfileSettingsForm;
};

export const readAllVlmProfileSettings = (
  config: unknown,
): Record<string, VlmProfileSettingsForm> =>
  Object.fromEntries(
    Object.entries(getVlmGatewayProfiles(config)).map(([name, profile]) => [
      name,
      readVlmProfileSettings(profile),
    ]),
  );

// Profiles whose settings Form mode may write. Writing beside a field spelled
// in other casing would leave two keys, and blockedReason only reports the
// first reason, so this checks the casing itself.
export const editableVlmProfileNames = (config: unknown): Set<string> => {
  if (hasMiscasedVlmGatewaySection(config)) {
    return new Set();
  }
  return new Set(
    Object.entries(getVlmGatewayProfiles(config))
      .filter(
        ([, profile]) =>
          isRecord(profile) && !hasMiscasedVlmProfileField(profile),
      )
      .map(([name]) => name),
  );
};

const validateNumber = (text: string, min: number): string | undefined => {
  if (!/^\d+$/.test(text) || Number(text) < min) {
    return `Must be a whole number of at least ${min}.`;
  }
  if (Number(text) > INT32_MAX) {
    return `Must be at most ${INT32_MAX}.`;
  }
  return undefined;
};

const parseJsonObject = (text: string): Record<string, unknown> | undefined => {
  try {
    const value: unknown = JSON.parse(text);
    return isRecord(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

const validateField = (
  field: VlmProfileSettingsField,
  text: string,
): string | undefined => {
  // The gateway can't parse an unknown provider, whitespace included, so only
  // a truly empty Provider means "not set".
  if (field === 'Provider') {
    return text === '' || canonicalProvider(text)
      ? undefined
      : 'Provider must be Claude, Gpt or Gemini.';
  }
  const trimmed = text.trim();
  if (trimmed === '') {
    return undefined;
  }
  if (isNumberField(field)) {
    return validateNumber(trimmed, NUMBER_FIELDS[field]);
  }
  if (isJsonField(field)) {
    return parseJsonObject(trimmed) ? undefined : 'Must be a JSON object.';
  }
  return undefined;
};

export const validateVlmProfileSettings = (
  form: VlmProfileSettingsForm,
): Partial<Record<VlmProfileSettingsField, string>> =>
  SETTINGS_FIELDS.reduce<Partial<Record<VlmProfileSettingsField, string>>>(
    (t, field) => {
      const error = validateField(field, form[field]);
      return error ? { ...t, [field]: error } : t;
    },
    {},
  );

export const invalidVlmProfileNames = (
  profiles: Record<string, VlmProfileSettingsForm>,
): string[] =>
  Object.keys(profiles)
    .filter(
      (name) =>
        Object.keys(validateVlmProfileSettings(profiles[name])).length > 0,
    )
    .sort((a, b) => a.localeCompare(b, 'en'));

// Expects text that passed validateVlmProfileSettings.
const toConfigValue = (
  field: VlmProfileSettingsField,
  text: string,
): unknown => {
  if (isNumberField(field)) {
    return Number(text.trim());
  }
  if (isJsonField(field)) {
    return JSON.parse(text) as unknown;
  }
  if (field === 'Provider') {
    return canonicalProvider(text) ?? text;
  }
  return field === 'Model' ? text.trim() : text;
};

const applyChangedFields = (
  profile: Record<string, unknown>,
  initial: VlmProfileSettingsForm,
  current: VlmProfileSettingsForm,
): Record<string, unknown> =>
  SETTINGS_FIELDS.filter((field) => current[field] !== initial[field]).reduce(
    (t, field) => {
      const text = current[field];
      if (text.trim() === '') {
        const { [field]: _removed, ...rest } = t;
        return rest;
      }
      return { ...t, [field]: toConfigValue(field, text) };
    },
    profile,
  );

const newProfile = (
  form: VlmProfileSettingsForm,
  id: string | undefined,
): Record<string, unknown> =>
  applyChangedFields(
    id === undefined ? {} : { Id: id },
    readVlmProfileSettings(undefined),
    form,
  );

// Profiles Form mode may add to this config: the section must be absent or
// well-formed, and a new name must not clash with a stored one in any casing.
const canAddVlmProfile = (
  config: Record<string, unknown>,
  profiles: Record<string, unknown>,
  name: string,
): boolean =>
  !hasMiscasedVlmGatewaySection(config) &&
  !hasMalformedVlmGatewaySection(config) &&
  isValidVlmProfileName(name) &&
  !Object.keys(profiles).some(
    (stored) => stored.toLowerCase() === name.toLowerCase(),
  );

// Writes only the fields whose text changed, so untouched profiles and fields
// stay exactly as stored. A profile in `current` but neither in `initial` nor
// in config is new and gets every non-empty field, and its Id from `newIds`.
export const applyVlmProfileSettings = (
  config: Record<string, unknown>,
  initial: Record<string, VlmProfileSettingsForm>,
  current: Record<string, VlmProfileSettingsForm>,
  newIds: Record<string, string> = {},
): Record<string, unknown> => {
  const profiles = getVlmGatewayProfiles(config);
  const nextProfiles = Object.keys(current).reduce((t, name) => {
    const profile = Object.hasOwn(profiles, name) ? profiles[name] : undefined;
    const before = Object.hasOwn(initial, name) ? initial[name] : undefined;
    if (isRecord(profile) && before) {
      return {
        ...t,
        [name]: applyChangedFields(profile, before, current[name]),
      };
    }
    if (!profile && !before && canAddVlmProfile(config, profiles, name)) {
      const id = Object.hasOwn(newIds, name) ? newIds[name] : undefined;
      return { ...t, [name]: newProfile(current[name], id) };
    }
    return t;
  }, profiles);

  if (nextProfiles === profiles) {
    return { ...config };
  }
  const gateway = isRecord(config.VlmGateway) ? config.VlmGateway : {};
  return { ...config, VlmGateway: { ...gateway, Profiles: nextProfiles } };
};

export const removeVlmProfiles = (
  config: Record<string, unknown>,
  names: readonly string[],
): Record<string, unknown> => {
  const profiles = getVlmGatewayProfiles(config);
  const removing = names.filter((name) => Object.hasOwn(profiles, name));
  if (removing.length === 0 || !isRecord(config.VlmGateway)) {
    return config;
  }

  const nextProfiles = Object.fromEntries(
    Object.entries(profiles).filter(([name]) => !removing.includes(name)),
  );
  return {
    ...config,
    VlmGateway: { ...config.VlmGateway, Profiles: nextProfiles },
  };
};

// Keeps each profile's place and content, Id included, so its key stays. Takes
// renames as vlmProfileRenames planned them against this config, so none clashes.
export const renameVlmProfiles = (
  config: Record<string, unknown>,
  renames: readonly VlmProfileRename[],
): Record<string, unknown> => {
  const profiles = getVlmGatewayProfiles(config);
  const applicable = new Map(
    renames
      .filter(({ from }) => Object.hasOwn(profiles, from))
      .map(({ from, to }) => [from, to]),
  );
  if (applicable.size === 0 || !isRecord(config.VlmGateway)) {
    return config;
  }

  const nextProfiles = Object.fromEntries(
    Object.entries(profiles).map(([name, profile]) => [
      applicable.get(name) ?? name,
      profile,
    ]),
  );
  return {
    ...config,
    VlmGateway: { ...config.VlmGateway, Profiles: nextProfiles },
  };
};

const NAME_RULE =
  'Use letters, digits and single dashes, 40 characters at most.';

export const validateNewVlmProfileName = (
  name: string,
  names: { existing: readonly string[]; removing: readonly string[] },
): string | undefined => {
  if (name === '') {
    return 'Enter a name.';
  }
  if (!isValidVlmProfileName(name)) {
    return NAME_RULE;
  }
  const sameIgnoringCase = (other: string) =>
    other.toLowerCase() === name.toLowerCase();
  const removing = names.removing.find(sameIgnoringCase);
  if (removing !== undefined) {
    return `"${removing}" is being removed. Undo the removal, or save first and then add it.`;
  }
  const existing = names.existing.find(sameIgnoringCase);
  if (existing === undefined) {
    return undefined;
  }
  return existing === name
    ? 'A profile with this name already exists.'
    : `Another profile is named "${existing}". Names must differ in more than letter case.`;
};
